import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@/shared/ui/material';

interface AlertOptions {
    message: string;
    severity?: AlertColor;
    duration?: number;
}

interface AlertContextValue {
    showAlert: (options: AlertOptions | string) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showWarning: (message: string) => void;
    showInfo: (message: string) => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

interface AlertProviderProps {
    children: ReactNode;
}

/**
 * 警告訊息 Provider
 * 包裹在 App 最外層，提供全域的 alert 功能
 */
export function AlertProvider({ children }: AlertProviderProps) {
    const [snackbarState, setSnackbarState] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
        duration: number;
    }>({
        open: false,
        message: '',
        severity: 'info',
        duration: 4000,
    });

    const showAlert = useCallback((options: AlertOptions | string) => {
        if (typeof options === 'string') {
            setSnackbarState({
                open: true,
                message: options,
                severity: 'info',
                duration: 4000,
            });
        } else {
            setSnackbarState({
                open: true,
                message: options.message,
                severity: options.severity || 'info',
                duration: options.duration || 4000,
            });
        }
    }, []);

    const showSuccess = useCallback(
        (message: string) => showAlert({ message, severity: 'success' }),
        [showAlert]
    );

    const showError = useCallback(
        (message: string) => showAlert({ message, severity: 'error', duration: 6000 }),
        [showAlert]
    );

    const showWarning = useCallback(
        (message: string) => showAlert({ message, severity: 'warning' }),
        [showAlert]
    );

    const showInfo = useCallback(
        (message: string) => showAlert({ message, severity: 'info' }),
        [showAlert]
    );

    const handleClose = useCallback((_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbarState((prev) => ({ ...prev, open: false }));
    }, []);

    return (
        <AlertContext.Provider value={{ showAlert, showSuccess, showError, showWarning, showInfo }}>
            {children}
            <Snackbar
                open={snackbarState.open}
                autoHideDuration={snackbarState.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleClose} severity={snackbarState.severity} variant="filled">
                    {snackbarState.message}
                </Alert>
            </Snackbar>
        </AlertContext.Provider>
    );
}

/**
 * 使用警告訊息的 Hook
 *
 * @example
 * const { showSuccess, showError, showAlert } = useAlert();
 *
 * // 簡單用法
 * showSuccess('儲存成功！');
 * showError('發生錯誤，請稍後再試');
 *
 * // 完整用法
 * showAlert({
 *     message: '自訂訊息',
 *     severity: 'warning',
 *     duration: 5000,
 * });
 */
export function useAlert(): AlertContextValue {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
}
